import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import countryCodes from '../data/countryCodes'; // Import des codes pays

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const SocieteEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [societe, setSociete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    address_societe: '',
    tel_societe: '', 
    code_postal_societe: '',
    statut_societe: 1
  });
  const [formErrors, setFormErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState('');
  
  // États pour le sélecteur d'indicatif
  const [showCountryList, setShowCountryList] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8,
      pattern: "^\\+227\\d{8}$"
    }
  );
  const [searchCountry, setSearchCountry] = useState("");
  const countryListRef = useRef(null);

  // Fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryListRef.current && !countryListRef.current.contains(event.target)) {
        setShowCountryList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchSociete();
  }, [id, navigate]);

  // Fonction pour extraire l'indicatif d'un numéro
  const extractCountryFromPhone = (phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Chercher le pays correspondant à l'indicatif
    const matchedCountry = countryCodes?.find(country => 
      country && phoneNumber.startsWith(country.code)
    );
    
    return matchedCountry || selectedCountry;
  };

  // Fonction pour extraire les chiffres seulement
  const extractPhoneDigits = (phoneNumber) => {
    if (!phoneNumber) return "";
    
    // Chercher l'indicatif
    const country = extractCountryFromPhone(phoneNumber);
    if (country) {
      return phoneNumber.replace(country.code, "");
    }
    
    return phoneNumber.replace(/\D/g, "");
  };

  // Fonction pour générer exemple
  const generatePhoneExample = (phoneLength) => {
    if (!phoneLength) return "";
    let example = "";
    for (let i = 0; i < phoneLength; i++) {
      example += (i % 10).toString();
    }
    return example;
  };

  const fetchSociete = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${API_BASE_URL}/societes/get.php`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          id: id
        }
      });

      console.log('📦 Réponse complète API sociétés:', response); // Debug complet
      console.log('📊 Données reçues:', response.data); // Debug des données

      if (response.data && response.data.success) {
        // CORRECTION ICI : Vérifier différentes structures possibles
        let societeData;
        
        if (response.data.data) {
          // Structure 1: { success: true, data: {...} }
          societeData = response.data.data;
        } else if (response.data.societe) {
          // Structure 2: { success: true, societe: {...} }
          societeData = response.data.societe;
        } else {
          // Structure 3: { success: true, ...directProps }
          societeData = response.data;
          // Supprimer les clés non nécessaires
          delete societeData.success;
          delete societeData.message;
        }
        
        console.log('✅ Données société extraites:', societeData);
        
        if (!societeData) {
          setError('❌ Structure de données invalide');
          return;
        }
        
        setSociete(societeData);

        if (societeData.logo_path) {
          setLogoPreview(`${API_BASE_URL}/${societeData.logo_path}`);
        }

        // Extraire l'indicatif et les chiffres du numéro
        const userCountry = extractCountryFromPhone(societeData.tel_societe || "");
        const userPhoneDigits = extractPhoneDigits(societeData.tel_societe || "");
        
        if (userCountry) {
          setSelectedCountry(userCountry);
        }
        
        // CORRECTION ICI : Vérifier que les champs existent avant de les utiliser
        setFormData({
          address_societe: societeData.address_societe || '',
          tel_societe: userPhoneDigits, // Stocker seulement les chiffres
          code_postal_societe: societeData.code_postal_societe || '',
          statut_societe: societeData.statut_societe || 1
        });
      } else {
        setError(`❌ ${response.data?.message || 'Erreur lors du chargement'}`);
      }
    } catch (error) {
      console.error('❌ Erreur détaillée fetchSociete:', error);
      
      // Messages d'erreur avec plus d'informations
      if (error.response) {
        console.log('🔍 Réponse erreur:', error.response);
        console.log('🔍 Statut:', error.response.status);
        console.log('🔍 Headers:', error.response.headers);
        console.log('🔍 Données:', error.response.data);
        
        if (error.response.status === 401) {
          setError('❌ Session expirée. Veuillez vous reconnecter.');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          setTimeout(() => navigate('/login'), 2000);
        } else if (error.response.status === 403) {
          setError('❌ Vous n\'avez pas la permission de voir cette société.');
          setTimeout(() => navigate('/societes'), 2000);
        } else if (error.response.status === 404) {
          setError('❌ Société non trouvée.');
          setTimeout(() => navigate('/societes'), 2000);
        } else if (error.response.data?.message) {
          setError(`❌ ${error.response.data.message}`);
        } else {
          setError(`❌ Erreur serveur (${error.response.status})`);
        }
      } else if (error.request) {
        console.log('🔍 Requête envoyée mais pas de réponse:', error.request);
        setError('❌ Serveur inaccessible. Vérifiez que le backend est démarré.');
      } else {
        console.log('🔍 Erreur de configuration:', error.message);
        setError(`❌ Erreur: ${error.message}`);
      }
      
      // Debug URL
      console.log('🔗 URL tentée:', `${API_BASE_URL}/societes/get.php?id=${id}`);
      console.log('🔑 Token (premiers 20 caractères):', localStorage.getItem('access_token')?.substring(0, 20) + '...');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'tel_societe') {
      // Déterminer la longueur max selon le pays
      const maxLength = selectedCountry?.phoneLength || 15;
      
      // Garder seulement les chiffres
      const digitsOnly = value.replace(/\D/g, "");
      // Limiter selon la longueur du pays
      const limitedDigits = digitsOnly.substring(0, maxLength);
      
      setFormData(prev => ({
        ...prev,
        [name]: limitedDigits
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.address_societe.trim()) {
      errors.address_societe = 'L\'adresse est obligatoire';
    }
    
    // Validation téléphone - DYNAMIQUE PAR PAYS
    const phoneDigits = formData.tel_societe.replace(/\D/g, "");
    const expectedLength = selectedCountry?.phoneLength;
    
    if (!phoneDigits) {
      errors.tel_societe = 'Le téléphone est obligatoire';
    } else if (!/^\d+$/.test(phoneDigits)) {
      errors.tel_societe = "Le numéro doit contenir uniquement des chiffres";
    } else if (expectedLength && phoneDigits.length !== expectedLength) {
      errors.tel_societe = `Numéro ${selectedCountry?.name || ""} invalide (${phoneDigits.length} chiffres, besoin de ${expectedLength} chiffres)`;
    } else if (!expectedLength && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
      errors.tel_societe = `Numéro invalide (${phoneDigits.length} chiffres, doit être entre 8 et 15 chiffres)`;
    }
    
    if (formData.code_postal_societe && !/^[0-9]{5}$/.test(formData.code_postal_societe)) {
      errors.code_postal_societe = 'Code postal invalide (5 chiffres requis)';
    }
    
    if (formData.statut_societe !== 0 && formData.statut_societe !== 1) {
      errors.statut_societe = 'Statut invalide';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 3 * 1024 * 1024;
    if (file.size > maxSize) {
      setLogoError('Fichier trop volumineux (max 3 Mo).');
      return;
    }

    setLogoError('');
    setLogoSuccess('');
    setLogoFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    setLogoError('');
    setLogoSuccess('');

    try {
      const token = localStorage.getItem('access_token');
      const formDataUpload = new FormData();
      formDataUpload.append('logo', logoFile);
      formDataUpload.append('id_societe', id);

      const response = await axios.post(`${API_BASE_URL}/societes/upload_logo.php`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.success) {
        setLogoSuccess('Logo mis à jour avec succès.');
        setLogoFile(null);
        // Le Header ne se remonte pas lors d'une navigation SPA : on le
        // prévient explicitement pour qu'il rafraîchisse le logo affiché
        // (même mécanisme que l'événement avatarUpdated côté profil).
        window.dispatchEvent(new CustomEvent('societeLogoUpdated', {
          detail: { logoBase64: logoPreview },
        }));
      } else {
        setLogoError(response.data?.message || "Erreur lors de l'envoi du logo.");
      }
    } catch (err) {
      setLogoError(err.response?.data?.message || "Erreur lors de l'envoi du logo.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }
    
    if (!window.confirm('Confirmez-vous la modification des informations de la société ?')) {
      return;
    }
    
    setUpdating(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Formater le numéro avec l'indicatif
      const phoneDigits = formData.tel_societe.replace(/\D/g, "");
      const phoneFullNumber = selectedCountry?.code + phoneDigits;
      
      console.log("Numéro téléphone société envoyé:", phoneFullNumber);

      const response = await axios.post(
        `${API_BASE_URL}/societes/update.php`,
        {
          id_societe: parseInt(id),
          address_societe: formData.address_societe,
          tel_societe: phoneFullNumber, // Utiliser le numéro formaté avec indicatif
          code_postal_societe: formData.code_postal_societe,
          statut_societe: parseInt(formData.statut_societe)
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('📦 Réponse mise à jour:', response.data);
      
      if (response.data.success) {
        setSuccess('✅ Société mise à jour avec succès !');

        if (response.data.societe) {
          setSociete(prev => ({
            ...prev,
            ...response.data.societe
          }));
        }

        // Un fichier de logo sélectionné mais pas encore envoyé (l'utilisateur
        // n'a pas cliqué sur le bouton dédié "Enregistrer le logo") est quand
        // même envoyé ici, pour qu'un seul clic sur "Enregistrer" suffise.
        if (logoFile) {
          await handleLogoUpload();
        }

        setTimeout(() => {
          navigate('/societes');
        }, 3000);
      } else {
        if (response.data.champs_bloques) {
          const blockedMsg = `Champs non modifiables: ${response.data.champs_bloques.join(', ')}`;
          setError(`❌ ${response.data.message}: ${blockedMsg}`);
        } else {
          setError(`❌ ${response.data.message}`);
        }
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // 🔍 Vérifier si c'est une erreur d'abonnement
        if (errorData.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {errorData.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError('')}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else if (errorData.champs_bloques) {
          const blockedMsg = `Champs non modifiables: ${errorData.champs_bloques.join(', ')}`;
          setError(`❌ ${errorData.message || 'Erreur'}: ${blockedMsg}`);
        } else {
          setError(`❌ ${errorData.message || 'Erreur lors de la mise à jour'}`);
        }
      } else if (error.response?.status === 403) {
        setError('❌ Vous n\'avez pas la permission de modifier cette société');
      } else if (error.response?.status === 404) {
        setError('❌ Société non trouvée');
      } else if (!error.response) {
        setError('❌ Serveur indisponible. Vérifiez votre connexion.');
      } else {
        setError('❌ Erreur inconnue: ' + error.message);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Annuler les modifications ?')) {
      navigate('/societes');
    }
  };

  /* ====== COMPOSANT COUNTRYSELECTOR ====== */
  const CountrySelector = ({ 
    showList, setShowList, selectedCountry, setSelectedCountry, 
    search, setSearch, listRef, disabled 
  }) => {
    // Filtrer les pays de manière SÉCURISÉE
    const filteredCountries = (countryCodes || []).filter(country => {
      if (!country) return false;
      
      const searchTerm = (search || "").toLowerCase();
      const countryName = String(country.name || country.label || "").toLowerCase();
      const countryCode = String(country.code || "").toLowerCase();
      
      return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
    });

    return (
      <div ref={listRef} className="position-relative">
        <button
          type="button"
          className="btn d-flex align-items-center justify-content-between"
          onClick={() => setShowList(!showList)}
          disabled={disabled}
          style={{
            backgroundColor: disabled ? '#F0F0F0' : '#F5F7FA',
            border: `1px solid ${formErrors.tel_societe ? '#f44336' : '#e0e0e0'}`,
            borderRadius: '8px',
            padding: '8px 12px',
            width: '120px',
            minHeight: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          <span className="me-2" style={{ fontSize: '1.2rem' }}>{selectedCountry?.flag || "🏳️"}</span>
          <span style={{ fontSize: '0.9rem', color: '#2E2E2E', fontWeight: '500' }}>
            {selectedCountry?.code || "+227"}
          </span>
          <i className="bi bi-chevron-down ms-2" style={{ 
            fontSize: '0.8rem', 
            color: disabled ? '#999' : '#666' 
          }}></i>
        </button>

        {showList && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: '0', width: '280px', maxHeight: '300px',
            backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden', marginTop: '4px'
          }}>
            <div className="p-2 border-bottom">
              <div className="input-group">
                <span className="input-group-text" style={{ 
                  backgroundColor: '#F5F7FA', borderColor: '#e0e0e0', padding: '8px 12px'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un pays..."
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: '0.9rem', borderColor: '#e0e0e0', padding: '8px 12px' }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredCountries.map((country, index) => {
                if (!country) return null;
                
                return (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-light text-start w-100 border-0 rounded-0"
                    onClick={() => {
                      if (country) {
                        setSelectedCountry(country);
                        setShowList(false);
                        setSearch("");
                      }
                    }}
                    style={{ 
                      padding: '10px 16px', 
                      borderBottom: '1px solid #f0f0f0', 
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {country?.flag || "🏳️"}
                      </span>
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '500' }}>
                          {country?.name || country?.label || "Pays"}
                        </div>
                        <small style={{ color: '#666' }}>{country?.code || ""}</small>
                        {country?.phoneLength && (
                          <small style={{ color: '#666', display: 'block' }}>
                            {country.phoneLength} chiffres
                          </small>
                        )}
                      </div>
                      {selectedCountry?.code === country?.code && <i className="bi bi-check text-primary"></i>}
                    </div>
                  </button>
                );
              })}
              
              {filteredCountries.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: '0.9rem' }}>
                  Aucun pays trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Chargement
  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: 'white', fontWeight: '600' }}>Modification Société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/dashboard")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Dashboard
                </button>
              </li>
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/societes")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Sociétés
                </button>
              </li>
              <li className="breadcrumb-item active" style={{ color: 'white' }}>Modification</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" style={{ color: '#0B3C5D' }} role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <h5 className="mt-4" style={{ color: '#0B3C5D' }}>Chargement de la société...</h5>
              <p className="mt-2" style={{ color: '#2E2E2E' }}>Veuillez patienter</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Erreur de chargement
  if (error && !societe) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: 'white', fontWeight: '600' }}>Modification Société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/dashboard")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Dashboard
                </button>
              </li>
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/societes")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Sociétés
                </button>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#f44336' }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-danger alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: '#f44336',
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid #f44336',
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">
                    <strong>Erreur :</strong> {error}
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => navigate('/societes')}
                    className="btn me-2"
                    style={{ 
                      backgroundColor: '#0B3C5D',
                      color: 'white',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1E88C8';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#0B3C5D';
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                  <button
                    onClick={fetchSociete}
                    className="btn"
                    style={{ 
                      color: '#0B3C5D',
                      borderColor: '#0B3C5D',
                      backgroundColor: 'transparent',
                      padding: '8px 20px',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0B3C5D';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#0B3C5D';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: 'white', fontWeight: '600' }}>Modification Société</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/dashboard")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Dashboard
              </button>
            </li>
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/societes")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Sociétés
              </button>
            </li>
            <li className="breadcrumb-item active" style={{ color: 'white' }}>
              {societe?.nom_societe || 'Société'}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-header text-center py-4" style={{ 
            backgroundColor: '#0B3C5D', 
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}>
            <h5 className="card-title mb-0 fs-4" style={{ color: 'white' }}>
              <i className="bi bi-building-gear me-2" style={{ color: 'white' }}></i>
              Modifier la société
            </h5>
            <p className="mb-0 mt-2" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {societe?.nom_societe}
            </p>
          </div>
          
          <div className="card-body p-4 p-lg-5">
            {/* Affichage des erreurs - IMPORTANT: avec whiteSpace pre-line et possibilité de JSX */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: '#f44336',
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid #f44336',
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError('')}
                  ></button>
                </div>
              </div>
            )}
            
            {success && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: '#4caf50',
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid #4caf50'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">{success}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccess('')}
                  ></button>
                </div>
                <div className="mt-2">
                  <div className="spinner-border spinner-border-sm me-2" style={{ color: '#4caf50' }}></div>
                  <small>Redirection vers la liste des sociétés...</small>
                </div>
              </div>
            )}

            {/* Vérifier si societe existe avant d'afficher */}
            {societe ? (
              <>
                {/* Logo de la société : affiché dans l'en-tête de l'application
                    (à la place du logo SecureControl) et sur les rapports PDF. */}
                <div className="card mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                  <div className="card-body">
                    <h6 style={{ color: '#0B3C5D', fontWeight: '600' }}>
                      <i className="bi bi-image me-2"></i>
                      Logo de la société
                    </h6>
                    <p className="text-muted small mb-3">
                      Affiché dans le menu de l'application et sur vos rapports exportés en PDF.
                      Choisissez un fichier puis cliquez sur "Enregistrer le logo" ci-dessous,
                      ou laissez-le sélectionné : il sera aussi envoyé quand vous cliquerez sur
                      "Enregistrer" en bas de page.
                    </p>

                    {logoError && <div className="alert alert-danger py-2">{logoError}</div>}
                    {logoSuccess && <div className="alert alert-success py-2">{logoSuccess}</div>}

                    <div className="d-flex align-items-center gap-4 flex-wrap">
                      <div className="d-flex align-items-center justify-content-center" style={{
                        width: '90px', height: '90px', borderRadius: '10px',
                        border: '1px dashed #c9d2dc', backgroundColor: '#F5F7FA', overflow: 'hidden'
                      }}>
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo société" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <i className="bi bi-building" style={{ fontSize: '2rem', color: '#c9d2dc' }}></i>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          className="form-control mb-2"
                          onChange={handleLogoChange}
                        />
                        <button
                          type="button"
                          className="btn btn-sm fw-semibold"
                          disabled={!logoFile || uploadingLogo}
                          onClick={handleLogoUpload}
                          style={{ backgroundColor: '#0B3C5D', color: 'white', borderRadius: '6px' }}
                        >
                          {uploadingLogo ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-upload me-1"></i>Enregistrer le logo</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info mb-4" style={{
                  backgroundColor: '#E3F2FD',
                  borderColor: '#4FC3F7',
                  color: '#0B3C5D',
                  borderRadius: '8px'
                }}>
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Informations non modifiables :</strong> 
                  <ul className="mb-0 mt-2">
                    <li><strong>Nom :</strong> {societe.nom_societe || 'Non spécifié'}</li>
                    <li><strong>Email :</strong> {societe.email_societe || 'Non spécifié'}</li>
                    <li><strong>NIF :</strong> {societe.nif_societe || 'Non renseigné'}</li>
                    <li><strong>RCCM :</strong> {societe.rccm_societe || 'Non renseigné'}</li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 mb-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#E3F2FD',
                          color: '#0B3C5D'
                        }}>
                          <i className="bi bi-geo-alt"></i>
                        </div>
                        <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '0' }}>
                          Informations de contact
                        </h6>
                      </div>
                    </div>

                    <div className="col-12 mb-4">
                      <label className="form-label fw-medium" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-geo-alt me-1" style={{ color: '#1E88C8' }}></i>
                        Adresse complète <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: formErrors.address_societe ? '#f44336' : '#e0e0e0',
                          color: formErrors.address_societe ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-geo-alt"></i>
                        </span>
                        <textarea
                          name="address_societe"
                          className={`form-control ${formErrors.address_societe ? 'is-invalid' : ''}`}
                          value={formData.address_societe}
                          onChange={handleChange}
                          placeholder="Adresse postale complète"
                          rows="3"
                          style={{ 
                            borderRadius: '0 8px 8px 0',
                            borderColor: formErrors.address_societe ? '#f44336' : '#e0e0e0',
                            color: '#2E2E2E'
                          }}
                          required
                        />
                      </div>
                      {formErrors.address_societe && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {formErrors.address_societe}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-medium" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-telephone me-1" style={{ color: '#1E88C8' }}></i>
                        Téléphone <span className="text-danger">*</span>
                      </label>
                      
                      <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
                        <CountrySelector
                          showList={showCountryList}
                          setShowList={setShowCountryList}
                          selectedCountry={selectedCountry}
                          setSelectedCountry={setSelectedCountry}
                          search={searchCountry}
                          setSearch={setSearchCountry}
                          listRef={countryListRef}
                          disabled={updating}
                        />
                        
                        <div className="position-relative" style={{ flex: '1' }}>
                          <div style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            fontSize: '0.95rem',
                            pointerEvents: 'none',
                            zIndex: 5
                          }}>
                            {selectedCountry?.code || "+227"}
                          </div>
                          <input
                            type="tel"
                            name="tel_societe"
                            className={`form-control ${formErrors.tel_societe ? 'is-invalid' : ''}`}
                            value={formData.tel_societe}
                            onChange={handleChange}
                            placeholder={selectedCountry?.phoneLength ? 
                              `Ex: ${generatePhoneExample(selectedCountry.phoneLength)}` : 
                              "Numéro de téléphone"}
                            disabled={updating}
                            maxLength={selectedCountry?.phoneLength || 15}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            style={{ 
                              borderRadius: '8px',
                              borderColor: formErrors.tel_societe ? '#f44336' : '#e0e0e0',
                              padding: '12px 16px 12px 55px',
                              fontSize: '0.95rem',
                              width: '100%',
                              color: '#2E2E2E'
                            }}
                            required
                          />
                        </div>
                      </div>
                      
                      {formErrors.tel_societe && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {formErrors.tel_societe}
                        </div>
                      )}
                      
                      <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                        Format: {selectedCountry?.code || "+227"} + {selectedCountry?.phoneLength || "?"} chiffres
                        {selectedCountry?.phoneLength && 
                          ` (ex: ${selectedCountry.code}${generatePhoneExample(selectedCountry.phoneLength)})`}
                      </small>
                    </div>

                    <div className="col-md-6 mb-4">
                      <label className="form-label fw-medium" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-mailbox me-1" style={{ color: '#1E88C8' }}></i>
                        Code postal
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#F5F7FA',
                          borderColor: formErrors.code_postal_societe ? '#f44336' : '#e0e0e0',
                          color: formErrors.code_postal_societe ? '#f44336' : '#1E88C8'
                        }}>
                          <i className="bi bi-mailbox"></i>
                        </span>
                        <input
                          type="text"
                          name="code_postal_societe"
                          className={`form-control ${formErrors.code_postal_societe ? 'is-invalid' : ''}`}
                          value={formData.code_postal_societe}
                          onChange={handleChange}
                          placeholder="Ex: 75000"
                          maxLength="5"
                          style={{ 
                            borderRadius: '0 8px 8px 0',
                            borderColor: formErrors.code_postal_societe ? '#f44336' : '#e0e0e0',
                            color: '#2E2E2E'
                          }}
                        />
                      </div>
                      {formErrors.code_postal_societe && (
                        <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {formErrors.code_postal_societe}
                        </div>
                      )}
                    </div>

                    {/* Boutons d'action */}
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center pt-4 border-top" style={{ borderColor: '#F5F7FA' }}>
                        <button
                          type="button"
                          className="btn fw-semibold"
                          onClick={handleCancel}
                          disabled={updating}
                          style={{ 
                            color: '#2E2E2E',
                            borderColor: '#e0e0e0',
                            backgroundColor: 'transparent',
                            padding: '10px 25px',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#F5F7FA';
                            e.target.style.borderColor = '#0B3C5D';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#e0e0e0';
                          }}
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Annuler
                        </button>
                        
                        <button
                          type="submit"
                          className="btn fw-semibold"
                          disabled={updating || Object.keys(formErrors).length > 0}
                          style={{ 
                            backgroundColor: updating || Object.keys(formErrors).length > 0 ? '#B0BEC5' : '#0B3C5D',
                            borderColor: updating || Object.keys(formErrors).length > 0 ? '#B0BEC5' : '#0B3C5D',
                            color: 'white',
                            padding: '10px 25px',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                            opacity: updating || Object.keys(formErrors).length > 0 ? 0.8 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!updating && Object.keys(formErrors).length === 0) {
                              e.target.style.backgroundColor = '#1E88C8';
                              e.target.style.borderColor = '#1E88C8';
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!updating && Object.keys(formErrors).length === 0) {
                              e.target.style.backgroundColor = '#0B3C5D';
                              e.target.style.borderColor = '#0B3C5D';
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {updating ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Mise à jour...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-save me-2"></i>
                              Enregistrer
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </>
            ) : (
              <div className="alert alert-warning" style={{ 
                backgroundColor: '#fff8e1',
                borderColor: '#ffc107',
                color: '#ff8f00',
                borderRadius: '8px'
              }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                Aucune donnée de société disponible. Veuillez réessayer.
                <div className="mt-2">
                  <button 
                    onClick={fetchSociete}
                    className="btn btn-sm"
                    style={{ 
                      color: '#0B3C5D',
                      borderColor: '#0B3C5D',
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0B3C5D';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#0B3C5D';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> Réessayer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong,
        h5, h6 {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small,
        p {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: #1E88C8;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: #F5F7FA;
          cursor: not-allowed;
        }
        
        .form-control.is-invalid {
          border-color: #f44336;
        }
        
        .invalid-feedback {
          color: #f44336;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SocieteEditPage;